// Modelo dos templates WhatsApp (HSM) da empresa — gestão, não envio.
// O seletor de envio do inbox tem tipos próprios em `features/inbox/api.ts`.

export type TemplateStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'PAUSED' | 'DISABLED';
export type TemplateCategory = 'UTILITY' | 'MARKETING' | 'AUTHENTICATION';

export type TemplateButton =
  | { type: 'QUICK_REPLY'; text: string }
  | { type: 'URL'; text: string; url: string; example?: string }
  | { type: 'PHONE_NUMBER'; text: string; phoneNumber: string };

export interface WhatsappTemplate {
  id: string;
  name: string;
  language: string;
  category: TemplateCategory;
  status: TemplateStatus;
  body: string;
  variables: string[];
  footer: string | null;
  buttons: TemplateButton[];
  rejectionReason: string | null;
  createdByName: string | null;
  submittedAt: string | null;
  approvedAt: string | null;
  createdAt: string;
}

/** Lista + por que ela pode estar vazia (mesmo formato do seletor do inbox). */
export interface TemplatesListResponse {
  items: WhatsappTemplate[];
  connected: boolean;
  reason?: 'NOT_CONNECTED';
}

export interface CreateTemplateInput {
  name: string;
  language: string;
  category: TemplateCategory;
  body: string;
  variables: string[];
  footer?: string;
  buttons: TemplateButton[];
}

/**
 * Tetos da Meta, replicados aqui só para o formulário avisar ANTES do round-trip.
 *
 * ⚠️ A autoridade é o backend (`chatia-api/src/infra/whatsapp/template-payload.ts`),
 * que revalida tudo e é quem fala com a Graph. Esta cópia existe porque são repos
 * separados; ao mudar um limite, mude nos dois — divergir aqui só torna o front mais
 * permissivo ou mais rígido que a verdade, nunca inseguro.
 */
export const TEMPLATE_LIMITS = {
  nameMax: 512,
  bodyMax: 1024,
  footerMax: 60,
  buttonTextMax: 25,
  buttonUrlMax: 2000,
  buttonPhoneMax: 20,
  buttonsMax: 10,
  urlButtonsMax: 2,
  phoneButtonsMax: 1,
} as const;

export const STATUS_LABEL: Record<TemplateStatus, string> = {
  PENDING: 'Em análise',
  APPROVED: 'Aprovado',
  REJECTED: 'Rejeitado',
  PAUSED: 'Pausado',
  DISABLED: 'Desativado',
};

export const CATEGORY_LABEL: Record<TemplateCategory, string> = {
  UTILITY: 'Utilidade',
  MARKETING: 'Marketing',
  AUTHENTICATION: 'Autenticação',
};

/** Só template aprovado pode ser enviado — é o que a tela sinaliza em verde. */
export function isSendable(status: TemplateStatus): boolean {
  return status === 'APPROVED';
}

/** Índices `{{n}}` distintos de um texto, na ordem de aparição. Espelha o back. */
export function extractVariableIndexes(text: string): number[] {
  const found = text.match(/\{\{\s*(\d+)\s*\}\}/g) ?? [];
  const seen = new Set<number>();
  const out: number[] = [];
  for (const raw of found) {
    const index = Number(raw.replace(/\D/g, ''));
    if (!seen.has(index)) {
      seen.add(index);
      out.push(index);
    }
  }
  return out;
}

/**
 * Substitui `{{n}}` pelos exemplos, para a prévia. Placeholder sem exemplo fica como
 * está — mostrar o `{{2}}` cru é mais honesto do que apagá-lo e dar a impressão de
 * que a mensagem sai com um buraco.
 */
export function renderPreview(body: string, examples: string[]): string {
  return body.replace(/\{\{\s*(\d+)\s*\}\}/g, (raw, n: string) => {
    const value = examples[Number(n) - 1];
    return value?.trim() ? value : raw;
  });
}
